import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { Dropdown, DropdownTrigger, DropdownMenu, DropdownItem, Button } from '@heroui/react';
import { DataTable, type ColumnMetaExt } from '@/components/common/DataTable';
import { DomainStatusChip } from '@/features/domains/components/DomainStatusChip';
import { formatRelative } from '@/lib/format';
import type { Domain } from '@/features/domains/types';
import { EllipsisVerticalIcon, ShieldCheckIcon, TrashIcon, GlobeAltIcon } from '@/icons';

export function DomainsTable({ domains, isLoading, isError, error, onRetry, sort, onSort, onVerify, onDelete }: {
  domains: Domain[]; isLoading: boolean; sort: string; onSort: (f: string) => void;
  onVerify: (d: Domain) => void; onDelete?: (d: Domain) => void;
  /** A failed load is shown as an error with retry, never as "No domains yet." */
  isError?: boolean; error?: unknown; onRetry?: () => void;
}) {
  const navigate = useNavigate();
  const columns = useMemo<ColumnDef<Domain, unknown>[]>(() => [
    {
      id: 'domain', header: 'Domain', meta: { sortKey: 'domain' } as ColumnMetaExt,
      cell: ({ row }) => (
        <div className="flex items-center gap-3">
          <span className="grid h-8 w-8 place-items-center rounded-lg bg-surface-raised text-fg-muted"><GlobeAltIcon className="h-4 w-4" /></span>
          <span className="font-medium text-fg">{row.original.domain}</span>
        </div>
      ),
    },
    { id: 'status', header: 'Status', cell: ({ row }) => <DomainStatusChip domain={row.original} /> },
    { id: 'catchAll', header: 'Catch-all', cell: ({ row }) => (
      <span className="text-sm text-fg-muted">{row.original.catchAllEnabled ? 'On' : 'Off'}</span>
    ) },
    { id: 'created', header: 'Added', meta: { sortKey: 'createdAt' } as ColumnMetaExt,
      cell: ({ row }) => <span className="text-sm text-fg-muted">{formatRelative(row.original.createdAt)}</span> },
    {
      id: 'actions', header: '', meta: { className: 'text-right w-12' } as ColumnMetaExt,
      cell: ({ row }) => (
        <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
          <Dropdown placement="bottom-end">
            <DropdownTrigger>
              <Button isIconOnly size="sm" variant="light" aria-label={`Actions for ${row.original.domain}`}>
                <EllipsisVerticalIcon className="h-5 w-5 text-fg-muted" />
              </Button>
            </DropdownTrigger>
            <DropdownMenu aria-label="Domain actions">
              {[
                <DropdownItem key="verify" startContent={<ShieldCheckIcon className="h-4 w-4" />} onPress={() => onVerify(row.original)}>Verify</DropdownItem>,
                // Delete is offered only when the caller passes onDelete (the user may delete domains).
                ...(onDelete ? [<DropdownItem key="delete" className="text-danger" color="danger" startContent={<TrashIcon className="h-4 w-4" />} onPress={() => onDelete(row.original)}>Delete</DropdownItem>] : []),
              ]}
            </DropdownMenu>
          </Dropdown>
        </div>
      ),
    },
  ], [onVerify, onDelete]);

  return (
    <DataTable<Domain>
      columns={columns} data={domains} getRowId={(d) => d.id} isLoading={isLoading} isError={isError} error={error} onRetry={onRetry}
      sort={sort} onSort={onSort} onRowClick={(d) => navigate(`/domains/${d.id}`)}
      emptyMessage="No domains yet."
    />
  );
}
